import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import * as XLSX from 'xlsx';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { PartyType } from '../../../party/party-type.enum';
import { PartyService } from '../../../party/party.service';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { JournalEntriesService } from '../journal-entries.service';
import { JournalEntry } from '../models/journal-entry-header.model';
import { JournalItem } from '../models/journal-entry-detail.model';
import { Journal } from '../models/journal.model';
import {
  ImportJournalEntryGroup,
  ImportJournalLineItem,
  ImportStep,
  ImportSummaryMetrics
} from './import-journal-entries.model';

@Component({
  selector: 'app-import-journal-entries',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ToastModule,
    ButtonModule,
    TableModule,
    TagModule,
    SelectModule,
    DatePickerModule,
    InputTextModule,
    InputNumberModule,
    ProgressBarModule,
    TooltipModule,
    DialogModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [
    JournalEntriesService,
    ChartOfAccountsService,
    SessionStorageService,
    PartyService,
    MessageService
  ],
  templateUrl: './import-journal-entries.component.html',
  styleUrls: ['./import-journal-entries.component.css']
})
export class ImportJournalEntriesComponent implements OnInit {
  companyId = 0;
  companyCurrency = 'SAR';

  activeStep: ImportStep = 1;
  selectedFile?: File;
  isLoading = false;
  isProcessing = false;

  canImport = false;

  journalList: Journal[] = [];
  chartOfAccounts: ChartOfAccounts[] = [];

  accountOptions: { label: string; value: string; account: ChartOfAccounts }[] = [];
  journalOptions: { label: string; value: string; journal: Journal }[] = [];

  // Default header values for Step 1
  defaultJournalId?: number;
  defaultEntryDate: Date = new Date();
  defaultCurrencyCode = 'SAR';

  // Step 2 & 3 state
  entries: ImportJournalEntryGroup[] = [];
  filterStatus: 'ALL' | 'VALID' | 'INVALID' = 'ALL';
  searchQuery = '';

  importedResults: JournalEntry[] = [];
  importSuccess = false;

  constructor(
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private journalEntriesService: JournalEntriesService,
    private chartOfAccountsService: ChartOfAccountsService,
    private partyService: PartyService,
    private permissionService: PermissionService,
    private messageService: MessageService
  ) {
    this.companyId = this.sessionStorageService.getCompany()?.id ?? 0;
  }

  ngOnInit(): void {
    this.canImport = this.permissionService.hasPermission(app_permissions.accounts.journal.create);
    this.loadCompanyCurrency();
    this.loadJournals();
    this.loadChartOfAccounts();
  }

  private loadCompanyCurrency(): void {
    const country = this.sessionStorageService.getCountryList()?.find(
      (c) => c.id === this.sessionStorageService.getCompany()?.countryId
    );
    this.companyCurrency = country?.currencyCode || 'SAR';
    this.defaultCurrencyCode = this.companyCurrency;
  }

  private loadJournals(): void {
    this.journalEntriesService.getAllJournalsByCompany(this.companyId).subscribe({
      next: (journals) => {
        this.journalList = journals || [];
        this.journalOptions = this.journalList.map((j) => ({
          label: `${j.journalCode} - ${j.journalName}`,
          value: j.journalCode,
          journal: j
        }));
        if (this.journalList.length > 0 && !this.defaultJournalId) {
          this.defaultJournalId = this.journalList[0].journalId;
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Journals Load Failed',
          detail: 'Could not load journals for the company.'
        });
      }
    });
  }

  private loadChartOfAccounts(): void {
    this.chartOfAccountsService.getAllChartOfAccountsByCompany(this.companyId, false).subscribe({
      next: (accounts) => {
        this.chartOfAccounts = accounts || [];
        this.accountOptions = this.chartOfAccounts.map((a) => ({
          label: `${a.accountCode} - ${a.accountName}`,
          value: a.accountCode,
          account: a
        }));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Accounts Load Failed',
          detail: 'Could not load chart of accounts for the company.'
        });
      }
    });
  }

  // --- Step 1: File selection & Template Download ---

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.selectedFile = input.files[0];
    }
  }

  downloadTemplate(format: 'xlsx' | 'csv' = 'xlsx'): void {
    const sampleJournalCode = this.journalList[0]?.journalCode || 'GEN';
    const sampleAccount1 = this.chartOfAccounts[0]?.accountCode || '1010';
    const sampleAccount2 = this.chartOfAccounts[1]?.accountCode || '2010';
    const sampleAccount3 = this.chartOfAccounts[2]?.accountCode || '5010';

    const sampleRows = [
      {
        'Entry Reference': 'JE-SAMPLE-01',
        'Date': new Date().toISOString().split('T')[0],
        'Journal Code': sampleJournalCode,
        'Account Code': sampleAccount1,
        'Description': 'Initial operational cash receipt',
        'Debit': 5000,
        'Credit': 0,
        'Currency': this.companyCurrency
      },
      {
        'Entry Reference': 'JE-SAMPLE-01',
        'Date': new Date().toISOString().split('T')[0],
        'Journal Code': sampleJournalCode,
        'Account Code': sampleAccount2,
        'Description': 'Accounts payable settlement offset',
        'Debit': 0,
        'Credit': 5000,
        'Currency': this.companyCurrency
      },
      {
        'Entry Reference': 'JE-SAMPLE-02',
        'Date': new Date().toISOString().split('T')[0],
        'Journal Code': sampleJournalCode,
        'Account Code': sampleAccount3,
        'Description': 'Office supplies expenditure',
        'Debit': 750,
        'Credit': 0,
        'Currency': this.companyCurrency
      },
      {
        'Entry Reference': 'JE-SAMPLE-02',
        'Date': new Date().toISOString().split('T')[0],
        'Journal Code': sampleJournalCode,
        'Account Code': sampleAccount1,
        'Description': 'Cash payment for supplies',
        'Debit': 0,
        'Credit': 750,
        'Currency': this.companyCurrency
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Journal Entries');

    if (format === 'csv') {
      XLSX.writeFile(workbook, `journal-entries-import-template.csv`, { bookType: 'csv' });
    } else {
      XLSX.writeFile(workbook, `journal-entries-import-template.xlsx`, { bookType: 'xlsx' });
    }
  }

  parseFileAndProceed(): void {
    if (!this.selectedFile) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No File Selected',
        detail: 'Please choose an Excel or CSV file to import.'
      });
      return;
    }

    this.isLoading = true;
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawRows = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
          defval: '',
          raw: false
        });

        if (!rawRows || rawRows.length === 0) {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Empty File',
            detail: 'The uploaded file does not contain any data rows.'
          });
          return;
        }

        this.processRawRows(rawRows);
        this.validateAllEntries();
        this.activeStep = 2;
        this.isLoading = false;
        this.messageService.add({
          severity: 'info',
          summary: 'Entries Parsed',
          detail: `Parsed ${this.entries.length} journal entry group(s). Review validation details.`
        });
      } catch (err: any) {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'File Parse Error',
          detail: err?.message || 'Failed to read spreadsheet file.'
        });
      }
    };

    reader.onerror = () => {
      this.isLoading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Read Error',
        detail: 'Could not read the uploaded file.'
      });
    };

    reader.readAsArrayBuffer(this.selectedFile);
  }

  // --- Spreadsheet Parsing & Grouping ---

  private processRawRows(rawRows: Record<string, any>[]): void {
    const groupsMap = new Map<string, ImportJournalEntryGroup>();
    let autoRefCounter = 1;

    for (let i = 0; i < rawRows.length; i++) {
      const row = rawRows[i];
      const ref = this.extractRowValue(row, [
        'Entry Reference',
        'Reference',
        'Entry Ref',
        'Entry No',
        'Entry Number',
        'Voucher No',
        'Transaction No',
        'Ref'
      ])?.trim() || `JE-IMPORT-${autoRefCounter}`;

      const rawDate = this.extractRowValue(row, [
        'Date',
        'Entry Date',
        'Posting Date',
        'Transaction Date'
      ]);
      const parsedDate = this.parseDate(rawDate) || this.defaultEntryDate || new Date();

      const journalCodeVal = this.extractRowValue(row, [
        'Journal Code',
        'Journal',
        'Journal Name'
      ])?.trim();
      const matchedJournal = this.findJournal(journalCodeVal);

      const currencyVal = this.extractRowValue(row, ['Currency', 'Currency Code'])?.trim()
        || this.defaultCurrencyCode;

      if (!groupsMap.has(ref)) {
        groupsMap.set(ref, {
          id: `entry-${Date.now()}-${autoRefCounter++}`,
          reference: ref,
          entryDate: parsedDate,
          journal: matchedJournal,
          journalCode: matchedJournal?.journalCode || journalCodeVal || '',
          currencyCode: currencyVal,
          items: [],
          totalDebit: 0,
          totalCredit: 0,
          balanceDifference: 0,
          isBalanced: false,
          isValid: false,
          errors: [],
          expanded: true
        });
      }

      const group = groupsMap.get(ref)!;

      const rawAccount = this.extractRowValue(row, [
        'Account Code',
        'Account',
        'GL Account',
        'COA',
        'Account No',
        'Account Number'
      ])?.trim();
      const matchedAccount = this.findAccount(rawAccount);

      const description = this.extractRowValue(row, [
        'Description',
        'Memo',
        'Narration',
        'Line Description',
        'Name'
      ])?.trim() || ref;

      const debitVal = this.parseNumber(
        this.extractRowValue(row, ['Debit', 'Debit Amount', 'Dr', 'Debit (Dr)'])
      );
      const creditVal = this.parseNumber(
        this.extractRowValue(row, ['Credit', 'Credit Amount', 'Cr', 'Credit (Cr)'])
      );

      const lineItem: ImportJournalLineItem = {
        id: `line-${Date.now()}-${i}`,
        accountCode: matchedAccount?.accountCode || rawAccount || '',
        account: matchedAccount,
        description: description,
        debit: debitVal,
        credit: creditVal,
        isValid: false,
        errors: []
      };

      group.items.push(lineItem);
    }

    this.entries = Array.from(groupsMap.values());
  }

  private extractRowValue(row: Record<string, any>, candidateKeys: string[]): any {
    for (const key of candidateKeys) {
      if (row[key] !== undefined && row[key] !== null && String(row[key]).trim() !== '') {
        return row[key];
      }
      // Check case-insensitive
      const foundKey = Object.keys(row).find((k) => k.trim().toLowerCase() === key.toLowerCase());
      if (foundKey && row[foundKey] !== undefined && row[foundKey] !== null && String(row[foundKey]).trim() !== '') {
        return row[foundKey];
      }
    }
    return '';
  }

  private parseDate(val: any): Date | null {
    if (!val) return null;
    if (val instanceof Date && !isNaN(val.getTime())) {
      return val;
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d;
    }
    // Try DD/MM/YYYY or DD-MM-YYYY
    const parts = String(val).split(/[-/]/);
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const customDate = new Date(year, month, day);
      if (!isNaN(customDate.getTime())) return customDate;
    }
    return null;
  }

  private parseNumber(val: any): number {
    if (val === undefined || val === null || val === '') return 0;
    if (typeof val === 'number') return isNaN(val) ? 0 : Math.max(0, val);
    const cleaned = String(val).replace(/[^0-9.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : Math.max(0, num);
  }

  private findAccount(codeOrName?: string): ChartOfAccounts | undefined {
    if (!codeOrName) return undefined;
    const clean = codeOrName.trim().toLowerCase();
    return this.chartOfAccounts.find(
      (a) =>
        a.accountCode?.toLowerCase() === clean ||
        a.accountName?.toLowerCase() === clean ||
        `${a.accountCode} ${a.accountName}`.toLowerCase().startsWith(clean)
    );
  }

  private findJournal(codeOrName?: string): Journal | undefined {
    if (!codeOrName) {
      return this.journalList.find((j) => j.journalId === this.defaultJournalId) || this.journalList[0];
    }
    const clean = codeOrName.trim().toLowerCase();
    const matched = this.journalList.find(
      (j) => j.journalCode?.toLowerCase() === clean || j.journalName?.toLowerCase() === clean
    );
    return matched || this.journalList.find((j) => j.journalId === this.defaultJournalId) || this.journalList[0];
  }

  // --- Step 2: Validation Engine & In-Place Editing ---

  validateAllEntries(): void {
    this.entries.forEach((entry) => this.validateEntry(entry));
  }

  validateEntry(entry: ImportJournalEntryGroup): void {
    const errors: string[] = [];

    // Header validations
    if (!entry.reference?.trim()) {
      errors.push('Reference is required.');
    }

    if (!entry.entryDate || isNaN(entry.entryDate.getTime())) {
      errors.push('Valid entry date is required.');
    }

    if (!entry.journal) {
      errors.push('Journal is required.');
    }

    if (!entry.items || entry.items.length < 2) {
      errors.push('Entry must contain at least 2 lines.');
    }

    let totalDebit = 0;
    let totalCredit = 0;

    // Line-level validations
    (entry.items || []).forEach((line, index) => {
      const lineErrors: string[] = [];

      // Account check
      if (!line.account) {
        lineErrors.push(`Line #${index + 1}: Account "${line.accountCode || 'Empty'}" not found in Chart of Accounts.`);
      }

      const debit = Number(line.debit || 0);
      const credit = Number(line.credit || 0);

      if (debit < 0 || credit < 0) {
        lineErrors.push(`Line #${index + 1}: Negative amounts are not allowed.`);
      }

      if (debit > 0 && credit > 0) {
        lineErrors.push(`Line #${index + 1}: Cannot have both Debit and Credit amounts on the same line.`);
      }

      if (debit === 0 && credit === 0) {
        lineErrors.push(`Line #${index + 1}: Either Debit or Credit must be greater than zero.`);
      }

      line.errors = lineErrors;
      line.isValid = lineErrors.length === 0;

      totalDebit += debit;
      totalCredit += credit;

      if (!line.isValid) {
        errors.push(...lineErrors);
      }
    });

    entry.totalDebit = Math.round(totalDebit * 100) / 100;
    entry.totalCredit = Math.round(totalCredit * 100) / 100;
    entry.balanceDifference = Math.round((entry.totalDebit - entry.totalCredit) * 100) / 100;

    // Balance check
    const diff = Math.abs(entry.balanceDifference);
    entry.isBalanced = diff < 0.005 && entry.totalDebit > 0;

    if (!entry.isBalanced) {
      if (entry.totalDebit === 0 && entry.totalCredit === 0) {
        errors.push('Total entry amount cannot be zero.');
      } else {
        errors.push(
          `Imbalance: Debits (${entry.totalDebit.toFixed(2)}) != Credits (${entry.totalCredit.toFixed(2)}). Difference: ${diff.toFixed(2)}`
        );
      }
    }

    entry.errors = errors;
    entry.isValid = errors.length === 0;
  }

  onFieldChange(entry: ImportJournalEntryGroup): void {
    this.validateEntry(entry);
  }

  onJournalChange(entry: ImportJournalEntryGroup): void {
    const selected = this.journalList.find((j) => j.journalCode === entry.journalCode);
    entry.journal = selected;
    this.validateEntry(entry);
  }

  onAccountChange(entry: ImportJournalEntryGroup, line: ImportJournalLineItem): void {
    const matched = this.chartOfAccounts.find((a) => a.accountCode === line.accountCode);
    line.account = matched;
    this.validateEntry(entry);
  }

  autoBalanceLine(entry: ImportJournalEntryGroup, targetLine: ImportJournalLineItem): void {
    let otherDebits = 0;
    let otherCredits = 0;

    for (const item of entry.items) {
      if (item !== targetLine) {
        otherDebits += Number(item.debit || 0);
        otherCredits += Number(item.credit || 0);
      }
    }

    if (otherDebits > otherCredits) {
      targetLine.credit = Math.round((otherDebits - otherCredits) * 100) / 100;
      targetLine.debit = 0;
    } else {
      targetLine.debit = Math.round((otherCredits - otherDebits) * 100) / 100;
      targetLine.credit = 0;
    }

    this.validateEntry(entry);
    this.messageService.add({
      severity: 'info',
      summary: 'Line Balanced',
      detail: `Adjusted line to balance entry ${entry.reference}.`
    });
  }

  addLine(entry: ImportJournalEntryGroup): void {
    const newLine: ImportJournalLineItem = {
      id: `line-${Date.now()}-${entry.items.length}`,
      accountCode: '',
      account: null,
      description: entry.reference,
      debit: 0,
      credit: 0,
      isValid: false,
      errors: []
    };
    entry.items.push(newLine);
    this.validateEntry(entry);
  }

  removeLine(entry: ImportJournalEntryGroup, lineIndex: number): void {
    if (entry.items.length <= 1) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Remove',
        detail: 'A journal entry must have at least one line.'
      });
      return;
    }
    entry.items.splice(lineIndex, 1);
    this.validateEntry(entry);
  }

  removeEntry(entryIndex: number): void {
    this.entries.splice(entryIndex, 1);
    this.messageService.add({
      severity: 'info',
      summary: 'Entry Discarded',
      detail: 'The entry was removed from the import list.'
    });
  }

  addManualEntry(): void {
    const defaultJournal = this.journalList.find((j) => j.journalId === this.defaultJournalId) || this.journalList[0];
    const newEntry: ImportJournalEntryGroup = {
      id: `manual-entry-${Date.now()}`,
      reference: `JE-MANUAL-${this.entries.length + 1}`,
      entryDate: new Date(),
      journal: defaultJournal,
      journalCode: defaultJournal?.journalCode || '',
      currencyCode: this.companyCurrency,
      items: [
        {
          id: `line-${Date.now()}-1`,
          accountCode: this.chartOfAccounts[0]?.accountCode || '',
          account: this.chartOfAccounts[0] || null,
          description: 'Manual entry line 1',
          debit: 0,
          credit: 0,
          isValid: false,
          errors: []
        },
        {
          id: `line-${Date.now()}-2`,
          accountCode: this.chartOfAccounts[1]?.accountCode || '',
          account: this.chartOfAccounts[1] || null,
          description: 'Manual entry line 2',
          debit: 0,
          credit: 0,
          isValid: false,
          errors: []
        }
      ],
      totalDebit: 0,
      totalCredit: 0,
      balanceDifference: 0,
      isBalanced: false,
      isValid: false,
      errors: [],
      expanded: true
    };
    this.validateEntry(newEntry);
    this.entries.unshift(newEntry);
  }

  // --- Filtering & Metrics ---

  get filteredEntries(): ImportJournalEntryGroup[] {
    return this.entries.filter((entry) => {
      if (this.filterStatus === 'VALID' && !entry.isValid) return false;
      if (this.filterStatus === 'INVALID' && entry.isValid) return false;

      if (this.searchQuery?.trim()) {
        const query = this.searchQuery.trim().toLowerCase();
        const matchesRef = entry.reference?.toLowerCase().includes(query);
        const matchesJournal = entry.journalCode?.toLowerCase().includes(query);
        const matchesLines = entry.items.some(
          (item) =>
            item.accountCode?.toLowerCase().includes(query) ||
            item.description?.toLowerCase().includes(query)
        );
        return matchesRef || matchesJournal || matchesLines;
      }

      return true;
    });
  }

  get summaryMetrics(): ImportSummaryMetrics {
    const totalEntries = this.entries.length;
    const validEntries = this.entries.filter((e) => e.isValid).length;
    const invalidEntries = totalEntries - validEntries;
    const totalDebits = this.entries.reduce((sum, e) => sum + e.totalDebit, 0);
    const totalCredits = this.entries.reduce((sum, e) => sum + e.totalCredit, 0);
    const totalDifference = Math.abs(totalDebits - totalCredits);

    return {
      totalEntries,
      validEntries,
      invalidEntries,
      totalDebits,
      totalCredits,
      totalDifference
    };
  }

  get metrics(): PageMetric[] {
    const summary = this.summaryMetrics;
    return [
      {
        label: 'Total Entries',
        value: summary.totalEntries,
        hint: `${summary.totalEntries} parsed`,
        tone: 'default'
      },
      {
        label: 'Valid Entries',
        value: summary.validEntries,
        hint: 'Ready to import',
        tone: 'success'
      },
      {
        label: 'Invalid Entries',
        value: summary.invalidEntries,
        hint: summary.invalidEntries > 0 ? 'Requires attention' : 'All balanced',
        tone: summary.invalidEntries > 0 ? 'danger' : 'info'
      },
      {
        label: 'Total Volume',
        value: `${this.companyCurrency} ${summary.totalDebits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        hint: summary.totalDifference === 0 ? 'Fully balanced' : `Net diff: ${summary.totalDifference.toFixed(2)}`,
        tone: summary.totalDifference === 0 ? 'success' : 'warn'
      }
    ];
  }

  // --- Step 3: Review & Batch Import ---

  proceedToReview(): void {
    this.validateAllEntries();
    const summary = this.summaryMetrics;

    if (summary.invalidEntries > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Entries Remain',
        detail: `There are ${summary.invalidEntries} invalid entry group(s). Correct them or remove them before importing.`
      });
    }

    this.activeStep = 3;
  }

  executeImport(): void {
    const validGroups = this.entries.filter((e) => e.isValid);

    if (validGroups.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'No Valid Entries',
        detail: 'There are no valid journal entries to import. Please correct entries in Step 2.'
      });
      return;
    }

    this.isProcessing = true;

    const companyParty = this.partyService.convertToParty(
      this.sessionStorageService.getCompany(),
      PartyType.Company,
      this.companyId
    );

    const payload: JournalEntry[] = validGroups.map((group) => {
      const entry = new JournalEntry();
      entry.reference = group.reference;
      entry.entryDate = group.entryDate;
      entry.journal = group.journal!;
      entry.currencyCode = group.currencyCode || this.companyCurrency;
      entry.amount = group.totalDebit;
      entry.state = 'posted';
      entry.owningCompanyId = this.companyId;
      entry.party = companyParty;

      entry.items = group.items.map((item) => {
        const detail = new JournalItem();
        detail.account = item.account!;
        detail.name = item.description || group.reference;
        detail.debit = Number(item.debit || 0);
        detail.credit = Number(item.credit || 0);
        detail.party = companyParty;
        return detail;
      });

      return entry;
    });

    this.journalEntriesService.createJournalEntriesBatch(this.companyId, payload).subscribe({
      next: (created) => {
        this.isProcessing = false;
        this.importSuccess = true;
        this.importedResults = created || [];
        this.messageService.add({
          severity: 'success',
          summary: 'Import Successful',
          detail: `Successfully imported ${this.importedResults.length} journal entries.`
        });
      },
      error: (err: any) => {
        this.isProcessing = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Import Failed',
          detail: err?.error?.message || err?.message || 'Failed to import journal entries batch.'
        });
      }
    });
  }

  // --- Navigation helpers ---

  goToStep(step: ImportStep): void {
    if (step === 2 && this.entries.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'Upload and parse a spreadsheet first.'
      });
      return;
    }
    this.activeStep = step;
  }

  viewImportedEntry(entry: JournalEntry): void {
    if (entry.entryId) {
      this.router.navigate(['/accounts/enterprise/journal-entries/view', entry.entryId]);
    }
  }

  navigateToJournalEntries(): void {
    this.router.navigate(['/accounts/enterprise/journal-entries']);
  }

  resetWizard(): void {
    this.activeStep = 1;
    this.selectedFile = undefined;
    this.entries = [];
    this.importedResults = [];
    this.importSuccess = false;
  }
}

